import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Clapperboard, X } from 'lucide-react';
import { api } from '../../lib/api';
import { useOverview } from '../../lib/live';
import { useT } from '../../lib/i18n';
import { gsap } from '../../motion/gsap';
import { shouldAnimate } from '../../lib/format';
import { playChime } from './chatNotify';
import { joinRoom, useActiveRoom } from './session';

/** "Mod invites you to watch …" with Join / Not now, wherever you are in the app. */
export function RoomInvitePopup() {
  const t = useT();
  const nav = useNavigate();
  const invites = useOverview((s) => s.data?.roomInvites);
  const refresh = useOverview((s) => s.refresh);
  const inRoom = useActiveRoom((s) => s.roomId);
  const box = useRef<HTMLDivElement>(null);
  const seen = useRef(new Set<string>());
  // Hide invites to the room you're already in.
  const shown = (invites ?? []).filter((i) => i.roomId !== inRoom).slice(0, 2);
  const key = shown.map((i) => i.id).join(',');

  useEffect(() => {
    const fresh = shown.filter((i) => !seen.current.has(i.id));
    fresh.forEach((i) => seen.current.add(i.id));
    if (!fresh.length) return;
    playChime();
    if (box.current && shouldAnimate()) gsap.from(box.current.children, { y: -16, opacity: 0, scale: 0.96, duration: 0.4, stagger: 0.06, ease: 'back.out(1.6)' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // An invite to the room you just joined is done with.
  useEffect(() => {
    for (const i of invites ?? []) if (i.roomId === inRoom) api.dismissRoomInvite(i.id).catch(() => undefined);
  }, [invites, inRoom]);

  if (!shown.length) return null;
  const close = (id: string) => api.dismissRoomInvite(id).finally(refresh);
  return createPortal(
    <div className="room-invites" ref={box} role="alert">
      {shown.map((i) => (
        <div className="room-invite" key={i.id}>
          <div className="room-invite-icon">
            <Clapperboard size={18} />
          </div>
          <div className="room-invite-text">
            <b>{t('invite.popup', { name: i.fromName.split(' ')[0] })}</b>
            <span>
              {i.roomName}
              {i.media && i.media !== i.roomName ? ` · ${i.media}` : ''}
            </span>
            {inRoom && <small>{t('invite.leavesCurrent')}</small>}
          </div>
          <div className="room-invite-actions">
            <button
              className="btn btn-accent btn-sm"
              onClick={() => {
                close(i.id);
                joinRoom(i.roomId, nav);
              }}
            >
              {t('room.join')}
            </button>
            <button className="room-invite-close" onClick={() => close(i.id)} aria-label={t('invite.notNow')} title={t('invite.notNow')}>
              <X size={15} />
            </button>
          </div>
        </div>
      ))}
    </div>,
    document.body,
  );
}
